/**
 * The do-not-call list, as a screen.
 *
 * Until now this list existed only as an effect: a number went on it from a
 * call's row menu, and after that it showed up as a struck-through phone number
 * wherever that number happened to be printed. Nothing could answer "who has
 * asked us to stop", which is the question a sales team is actually asked - by
 * a colleague about to run a campaign, and by the person themselves when they
 * ring back a month later to check we wrote it down.
 *
 * It lives inside the call desk rather than as a page of its own because it is
 * read while working the phone, and because the rail beside it is the way back
 * to the log. The whole list arrives in one request - it grows only when
 * somebody refuses, so it is dozens of rows - which is why the search and the
 * source filter are local (`rows.ts`) and there is no paging.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, PhoneOff, Plus, Search, SlidersHorizontal } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import {
  listPhoneSuppression,
  removePhoneSuppression,
  type PhoneSuppression,
} from "../../../../lib/callSuppression";
import { formatPhone } from "../../../../lib/phone";
import { Btn, EmptyState, IconBtn, fmtNum, listCard } from "../../../ui";
import { Pop, useClosePops } from "../../../shell/Pop";
import { ConfirmModal } from "../../_shared/ConfirmModal";
import { RailTrigger } from "../../_shared/workMobile";
import { AddNumberModal } from "./AddNumberModal";
import { DoNotCallRow } from "./DoNotCallRow";
import {
  ANY_SOURCE,
  DEFAULT_DO_NOT_CALL_FILTER,
  DO_NOT_CALL_SOURCES,
  doNotCallFilterActive,
  filterDoNotCall,
  permanentCount,
  type DoNotCallFilter,
} from "./rows";

/** Who, why, the note, the calls behind it, when it was written down, and the
 *  menu. Shared by the head and every row so a column cannot move in one
 *  without moving in the other. */
const COLS = "minmax(0,2.1fr) 1fr minmax(0,1.5fr) 0.9fr 1.1fr 44px";

export function DoNotCallPanel({
  canAdd,
  canRemove,
  onRail,
  /** Told when the list changes, so the shell's struck-through numbers agree
   *  with this screen without either of them refetching. */
  onAdded,
  onRemoved,
}: {
  /**
   * Two rights, not one, and the seam is the DIRECTION rather than
   * read-versus-write - the same seam the routes behind them use (§3.8).
   *
   * Putting a number ON the list is `use:crm.calls`, because the person who
   * heard "stop calling me" is the one holding the phone and they have to be
   * able to honour it in that minute. Taking one OFF makes somebody callable
   * again, which is the direction that can do harm, so it is `manage:crm.calls`.
   *
   * One `canManage` would have had to pick one of the two, and either choice is
   * wrong: it either hides Add from the people who need it most, or offers
   * Remove to everybody and lets the server say no afterwards.
   */
  canAdd: boolean;
  canRemove: boolean;
  onRail: () => void;
  onAdded: (value: string) => void;
  onRemoved: (value: string) => void;
}) {
  const t = useT();
  const [rows, setRows] = useState<PhoneSuppression[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<DoNotCallFilter>(DEFAULT_DO_NOT_CALL_FILTER);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmDel, setConfirmDel] = useState<PhoneSuppression | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      // `context` is what turns a list of numbers into a list of companies. It
      // costs two more indexed reads and only this screen asks for it.
      const res = await listPhoneSuppression({ context: true });
      setRows(res.rows);
      setError(null);
    } catch {
      setRows([]);
      setError(t("calls.suppression.loadError"));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => filterDoNotCall(rows ?? [], filter), [rows, filter]);
  const narrowed = doNotCallFilterActive(filter);

  async function remove(row: PhoneSuppression) {
    try {
      await removePhoneSuppression(row.id);
      setRows((cur) => (cur ?? []).filter((r) => r.id !== row.id));
      onRemoved(row.value);
      setError(null);
    } catch {
      setError(t("calls.suppression.removeRefused"));
    } finally {
      setConfirmDel(null);
    }
  }

  return (
    <div className="crm-card crm-work-col crm-work-list" style={listCard(COLS)}>
      {/* The same number strip the call log wears, answering the three
          questions this list is opened with: how many, how many of them are
          nobody's to undo, and how many nobody has ever called. */}
      <div className="crm-cstats">
        <div className="crm-cstat">
          <b>{fmtNum(rows?.length ?? 0)}</b>
          <span>{t("calls.suppression.statTotal")}</span>
        </div>
        <div className="crm-cstat">
          <b>{fmtNum(permanentCount(rows ?? []))}</b>
          <span>{t("calls.suppression.statPermanent")}</span>
        </div>
        <div className="crm-cstat">
          <b>{fmtNum((rows ?? []).filter((r) => !r.party || r.party.calls === 0).length)}</b>
          <span>{t("calls.suppression.statNeverCalled")}</span>
        </div>
      </div>

      <div className="crm-pane-head pt-3.5">
        <RailTrigger label={t("calls.pane.views")} onOpen={onRail} />
        <label className="crm-pane-search">
          <Search className="size-4" />
          <input
            type="search"
            value={filter.q}
            onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            placeholder={t("calls.suppression.searchPlaceholder")}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        {rows === null ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}

        <SourceFilter filter={filter} onFilter={setFilter} />

        {canAdd ? (
          <Btn small tone="brand" onClick={() => setShowAdd(true)}>
            <Plus className="size-4" />
            {t("calls.suppression.add")}
          </Btn>
        ) : null}
      </div>

      {error ? (
        <div className="mx-4 mb-2 flex items-center gap-2 text-[12.5px] text-destructive">
          <AlertTriangle className="size-3.5" />
          {error}
        </div>
      ) : null}

      <div className="crm-pane-scroll">
        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : shown.length === 0 ? (
          // An empty list and an empty search are different facts. Saying
          // "nobody has refused" over three active filters sends somebody
          // looking for a bug in the marking, which works.
          <EmptyState
            icon={PhoneOff}
            title={t(narrowed ? "calls.suppression.noMatch" : "calls.suppression.empty")}
            body={t(narrowed ? "calls.suppression.noMatchBody" : "calls.suppression.emptyBody")}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 6 }}>
              <span>{t("calls.suppression.col.who")}</span>
              <span>{t("calls.suppression.col.source")}</span>
              <span>{t("calls.suppression.col.note")}</span>
              <span>{t("calls.suppression.col.calls")}</span>
              <span>{t("calls.suppression.col.added")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {shown.map((row) => (
                <DoNotCallRow
                  key={row.id}
                  row={row}
                  cols={COLS}
                  canRelease={canRemove}
                  onRemove={() => setConfirmDel(row)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {showAdd ? (
        <AddNumberModal
          onClose={() => setShowAdd(false)}
          onAdded={(row) => {
            setShowAdd(false);
            onAdded(row.value);
            // Re-read rather than splicing the answer in: the POST upserts, so
            // marking a number that is already on the list answers with the row
            // that was already there and splicing would draw it twice.
            void load();
          }}
          onError={() => {
            setShowAdd(false);
            setError(t("calls.suppression.addError"));
          }}
        />
      ) : null}

      {confirmDel ? (
        <ConfirmModal
          title={t("calls.suppression.removeTitle")}
          body={t("calls.suppression.confirmRemove", { phone: formatPhone(confirmDel.value) })}
          confirmLabel={t("calls.suppression.remove")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => remove(confirmDel)}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}
    </div>
  );
}

/** Why the number is on the list, as the head's one filter. A menu rather than
 *  a row of pills: the head already carries a search box and an action, and the
 *  four sources are read once and then left alone. */
function SourceFilter({
  filter,
  onFilter,
}: {
  filter: DoNotCallFilter;
  onFilter: (next: DoNotCallFilter) => void;
}) {
  const t = useT();
  const close = useClosePops();
  const active = filter.source !== ANY_SOURCE;

  return (
    <Pop
      trigger={(open) => (
        <IconBtn
          className={cn(active && "crm-cb-dot", open && "is-on")}
          title={t("calls.suppression.filterTitle")}
          aria-label={t("calls.suppression.filterTitle")}
          aria-expanded={open}
        >
          <SlidersHorizontal className="size-4" />
        </IconBtn>
      )}
    >
      <div className="crm-menu-head">
        <div className="crm-menu-t">{t("calls.suppression.filterTitle")}</div>
        <div className="crm-menu-s">{t("calls.suppression.filterSub")}</div>
      </div>

      <button
        type="button"
        className={cn("crm-mi", filter.source === ANY_SOURCE && "is-on")}
        onClick={() => {
          onFilter({ ...filter, source: ANY_SOURCE });
          close();
        }}
      >
        {t("calls.suppression.sourceAny")}
      </button>
      {DO_NOT_CALL_SOURCES.map((source) => (
        <button
          key={source}
          type="button"
          className={cn("crm-mi", filter.source === source && "is-on")}
          onClick={() => {
            onFilter({ ...filter, source });
            close();
          }}
        >
          {t(`calls.suppression.source.${source}`)}
        </button>
      ))}
    </Pop>
  );
}
